import React, { useMemo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { Dayjs } from 'dayjs';
import { useTheme } from '@mui/material/styles';
import { ReactComponent as RightArrowBoxIcon } from '../../../assets/PepWise2.0/Right-Arrow-Icon-Box.svg';
import { ReactComponent as LeftArrowBoxIcon } from '../../../assets/PepWise2.0/Left-Arrow-Icon-Box.svg';

const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

type CalendarProps = {
  month: Dayjs;
  setMonth: React.Dispatch<React.SetStateAction<Dayjs>>;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  hoverDate: Dayjs | null;
  setHoverDate: React.Dispatch<React.SetStateAction<Dayjs | null>>;
  onDateClick: (date: Dayjs) => void;
  isDisabledDate: (date: Dayjs) => boolean;
  inRange: (date: Dayjs) => boolean;
  isStart: (date: Dayjs) => boolean;
  isEnd: (date: Dayjs) => boolean;
  canGoPrevMonth: boolean;
  canGoNextMonth: boolean;
};

const getMonthMatrix = (month: Dayjs) => {
  const start = month.startOf('month').startOf('week');
  const end = month.endOf('month').endOf('week');

  let current = start.clone();
  const matrix: Dayjs[][] = [];

  while (current.isBefore(end) || current.isSame(end, 'day')) {
    const week: Dayjs[] = [];

    for (let i = 0; i < 7; i++) {
      week.push(current.clone());
      current = current.add(1, 'day');
    }

    matrix.push(week);
  }

  return matrix;
};

export default function Calendar({
  month,
  setMonth,
  startDate,
  endDate,
  hoverDate,
  setHoverDate,
  onDateClick,
  isDisabledDate,
  inRange,
  isStart,
  isEnd,
  canGoPrevMonth,
  canGoNextMonth,
}: CalendarProps) {
  const theme = useTheme();
  const colors = theme.palette;

  const matrix = useMemo(() => getMonthMatrix(month), [month]);

  return (
    <Box
      sx={{
        p: 2,
        width: 260,
        background: `${colors.dateFilter.calendarBackground} !important`,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <IconButton
          size="small"
          onClick={() => canGoPrevMonth && setMonth((prev) => prev.subtract(1, 'month'))}
          disabled={!canGoPrevMonth}
        >
          <RightArrowBoxIcon />
        </IconButton>

        <Typography fontWeight={600} sx={{ color: colors.dateFilter.calendarHeaderText }}>
          {month.format('MMM, YYYY')}
        </Typography>

        <IconButton
          size="small"
          onClick={() => canGoNextMonth && setMonth((prev) => prev.add(1, 'month'))}
          disabled={!canGoNextMonth}
        >
          <LeftArrowBoxIcon />
        </IconButton>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', mb: 1 }}>
        {weekDays.map((d) => (
          <Typography
            key={d}
            fontSize={12}
            textAlign="center"
            sx={{ color: colors.dateFilter.weekDayText }}
          >
            {d}
          </Typography>
        ))}
      </Box>

      {matrix.map((week, i) => (
        <Box key={i} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
          {week.map((date) => {
            const start = isStart(date);
            const end = isEnd(date);
            const range = inRange(date);
            const disabledDate = isDisabledDate(date);
            const outsideMonth = !date.isSame(month, 'month');

            return (
              <Box
                key={date.toString()}
                onClick={() => onDateClick(date)}
                onMouseEnter={() => {
                  if (!disabledDate) setHoverDate(date);
                }}
                sx={{
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: disabledDate ? 'not-allowed' : 'pointer',
                  backgroundColor: range ? colors.dateFilter.rangeBackground : 'transparent',
                  borderTopLeftRadius: start ? '4px' : 0,
                  borderBottomLeftRadius: start ? '4px' : 0,
                  borderTopRightRadius: end ? '4px' : 0,
                  borderBottomRightRadius: end ? '4px' : 0,
                  opacity: disabledDate ? 0.35 : outsideMonth ? 0.65 : 1,
                  pointerEvents: 'auto',
                  '&:hover': {
                    backgroundColor: disabledDate
                      ? 'transparent'
                      : `${colors.dateFilter.dayHoverBackground} !important`,
                    color: disabledDate
                      ? 'inherit'
                      : `${colors.dateFilter.dayHoverText} !important`,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    backgroundColor: 'transparent',
                    color: disabledDate
                      ? colors.dateFilter.dayText
                      : range
                      ? colors.dateFilter.dayTextInRange
                      : colors.dateFilter.dayText,
                    '&:hover': {
                      color: disabledDate
                        ? colors.dateFilter.dayText
                        : `${colors.dateFilter.dayHoverText} !important`,
                    },
                  }}
                >
                  {date.date()}
                </Box>
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}
